import { Data as PageViewData } from 'modules/Analytics/helpers/pageView';

type PlaybackErrorProps = {
  errorMessage: string;
  errorType: 'network_error' | 'playback_failure';
};

type StreamEndProps = {
  endReason: string;
  listenTime: number;
};

type StreamStartProps = {
  playbackStartTime: number;
  streamInitTime: number;
};

type TrackingData = {
  item: {
    asset: {
      id: string;
      name?: string;
      sub: {
        id: string;
        name: string;
      };
    };
  };
  station: {
    asset: TrackingData['item']['asset'];
    playedFrom?: number;
  };
};

type PodcastPageViewDataProps = {
  pageName: string;
  podcastId: string;
  name?: string;
};

export type SocialPlatform = 'facebook' | 'x';

type SocialShareDataProps = {
  id: string | number;
  name?: string;
  platform: string;
  subId?: string | number;
  subName?: string;
  type: string;
  subType?: string;
};

/**
 * Maps component props to data fields for Podcast PageView event
 */
export function generatePodcastPageViewTrackingData({
  name,
  pageName,
  podcastId,
}: PodcastPageViewDataProps): PageViewData {
  let pageHost = '';
  let pageURL = '';

  if (__CLIENT__ && document.referrer) {
    const { host, href } = new URL(document.referrer);
    pageHost = host;
    pageURL = href;
  }

  return {
    contentFrame: 'responsive_widget',
    id: `podcast|${podcastId}`,
    inNetwork: pageHost.includes('iheart').toString(),
    name,
    pageHost,
    pageName,
    pageURL,
  };
}

/**
 * Maps component props to data fields for Play/Pause events
 * Also is base payload for other analytics calls
 */
export function generatePodcastPlaybackTrackingData({
  episodeId,
  name,
  playedFrom,
  podcastId,
  subName,
}: {
  episodeId: string;
  name?: string;
  playedFrom?: number;
  podcastId: string;
  subName: string;
}): TrackingData {
  return {
    item: {
      asset: {
        id: `podcast|${podcastId}`,
        name,
        sub: {
          id: `episode|${episodeId}`,
          name: subName,
        },
      },
    },
    station: {
      asset: {
        id: `podcast|${podcastId}`,
        name,
        sub: {
          id: `episode|${episodeId}`,
          name: subName,
        },
      },
      playedFrom,
    },
  };
}

export function getPodcastPlaybackAnalyticsPayload(
  trackingData: TrackingData,
  data: PlaybackErrorProps | StreamEndProps | StreamStartProps,
): {
  item: TrackingData['item'];
  station: TrackingData['station'] &
    (PlaybackErrorProps | StreamEndProps | StreamStartProps);
} {
  return {
    ...trackingData,
    station: {
      ...trackingData.station,
      ...data,
    },
  };
}

/**
 * Maps component props to data fields for share event (e.g. FB share or X share)
 */
export function generateSocialShareTrackingData({
  id,
  name,
  platform,
  subId,
  subName,
  type,
  subType,
}: SocialShareDataProps) {
  return {
    share: {
      platform,
    },
    station: {
      asset: {
        id: `${type}|${id}`,
        name,
        ...(subId && {
          sub: {
            id: `${subType}|${subId}`,
            name: subName,
          },
        }),
      },
    },
  };
}
